import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import WarningsModal from './dialogs/WarningsModal';
import '../ModelSpreadsheet.scss';

const DB = 'DB';
const ORIGINAL = 'OriginalCTRAC';

// Convert a 0xAARRGGBB ARGB value from Constants.ColorTable into a CSS rgb() string.
function colorToCss(color) {
  if (color == null) return null;
  const argb = Number(color);
  if (!Number.isFinite(argb)) return null;
  const r = (argb >> 16) & 0xff;
  const g = (argb >> 8) & 0xff;
  const b = argb & 0xff;
  return `rgb(${r}, ${g}, ${b})`;
}

// Warning icon button used to flag a calculation that removed one or more
// rows by date alignment. Clicking the icon opens the
// <WarningsModal> dialog so the admin can inspect the full list.
// Renders nothing when count is zero or the calc id is missing.
function RemovedRowsWarningIcon({ count, onClick, titlePrefix = 'Calculation' }) {
  if (!count || count <= 0) return null;
  const fullTitle = `${titlePrefix} removed ${count} row${count === 1 ? '' : 's'} during date alignment. Click to see details.`;
  return (
    <button
      type="button"
      className="removed-rows-warning-icon-btn"
      onClick={onClick}
      title={fullTitle}
      aria-label={fullTitle}
    >
      <span className="removed-rows-warning-icon" role="img" aria-hidden="true">
        ⚠
      </span>
      <span className="removed-rows-warning-count">{count}</span>
    </button>
  );
}

// -------------------------------------------------------------------------
// Consolidated / row-by-row comparison helpers
// -------------------------------------------------------------------------

// Display mode for the comparison panel.
const VIEW_MODE_ALL = 'all'; // side-by-side paginated grids (original mode)
const VIEW_MODE_CONSOLIDATED = 'consolidated'; // single merged table
const VIEW_MODE_DIFFERENCES = 'differences'; // merged table, only rows with any difference

// The server emits the join key column's label in the response so we can
// show "Join key: DATE" in the panel footer without re-deriving it client-
// side. The headers come in as "DB-A" / "Original-A" pairs — the panel
// re-collapses them into one column per spreadsheet column for rendering,
// then expands each row back out into the two visible halves.

// Returns [{ key, meta, dbHeader, origHeader }] for rendering. Each entry
// represents one spreadsheet column paired with its DB / Original CSV
// halves; `meta` is the spreadsheet column metadata (Label + Title + Tag)
// used to render the header exactly like the "Show all" per-side panel.
function stripHeaderPrefix(s, prefix) {
  if (!s) return '';
  return s.startsWith(prefix) ? s.substring(prefix.length) : s;
}

function collapseHeaders(headers, columns) {
  const pairs = [];
  // The server emits two CSV columns per spreadsheet column, in order.
  // Walk them in steps of 2 and pair DB-* with Original-*. The matching
  // column metadata (if present) rides along in the same index order so
  // the panel can render Label + Title + Tag without re-deriving them.
  const columnList = columns || [];
  for (let i = 0; i < headers.length; i += 2) {
    const dbHeader = headers[i] || '';
    const origHeader = headers[i + 1] || '';
    const meta = columnList[i / 2] || null;
    pairs.push({
      key: `${dbHeader}__${origHeader}`,
      meta,
      dbHeader,
      origHeader,
    });
  }
  return pairs;
}

// Given a CSV row from the server (one row = array of strings aligned with
// `headers`), produce the "DB | Original" cell display and a flag
// indicating whether the cell differs from its counterpart.
function expandCell(dbText, origText) {
  const dbMissing = dbText == null || dbText === '';
  const origMissing = origText == null || origText === '';
  let display;
  if (dbMissing && origMissing) display = '- | -';
  else if (dbMissing) display = `- | ${origText}`;
  else if (origMissing) display = `${dbText} | -`;
  else display = `${dbText} | ${origText}`;
  const isDifferent =
    dbMissing !== origMissing ||
    (!dbMissing && !origMissing && dbText !== origText);
  return { display, isDifferent };
}

function formatDuration(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') {
    if (value >= 60_000) return `${(value / 60_000).toFixed(1)} min`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(2)}s`;
    return `${Math.round(value)}ms`;
  }
  if (typeof value === 'object' && typeof value.TotalMilliseconds === 'number') {
    const totalMs = value.TotalMilliseconds;
    if (totalMs >= 60_000) return `${(totalMs / 60_000).toFixed(1)} min`;
    if (totalMs >= 1_000) return `${(totalMs / 1_000).toFixed(2)}s`;
    return `${Math.round(totalMs)}ms`;
  }
  return String(value);
}

function formatDateTime(value) {
  if (value == null) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

// Render the merged row-by-row table. Reads from the backend CSV cache
// (one page at a time) and presents "DB | Original" pairs. Differing
// cells are highlighted in light red. When `differencesOnly` is true,
// rows where every cell matches are hidden — the panel knows the page
// only contains such rows when the server-side differences filter
// returned them, so no extra client-side filter is needed.
function ConsolidatedComparisonPanel({
  dbCalc,
  origCalc,
  columns,
  headers,
  rows,
  totalRowCount,
  page,
  pageSize,
  loading,
  error,
  differencesOnly,
  builtAtUtc,
  onPageChange,
  onShowRemovedRows,
}) {
  const collapsedHeaders = useMemo(
    () => collapseHeaders(headers || [], columns),
    [headers, columns]
  );

  // Local state for the "jump to page" input. Kept in sync with the
  // current page so users always see where they are, but a draft value
  // (incomplete or out-of-range) is allowed while typing so the field
  // doesn't fight the user mid-edit.
  const totalPages = Math.max(1, Math.ceil((totalRowCount || 0) / pageSize));
  const [pageInputValue, setPageInputValue] = useState(String(page + 1));
  useEffect(() => {
    setPageInputValue(String(page + 1));
  }, [page]);

  function commitPageInput() {
    const parsed = parseInt(pageInputValue, 10);
    if (!Number.isFinite(parsed)) {
      setPageInputValue(String(page + 1));
      return;
    }
    const target = Math.min(totalPages, Math.max(1, parsed));
    setPageInputValue(String(target));
    onPageChange(target - 1);
  }

  // Per-row diff flag (any cell differs?) drives the row-level styling.
  const rowsWithFlags = useMemo(() => {
    return (rows || []).map((row, idx) => {
      const cells = [];
      let hasAnyDifference = false;
      for (let i = 0; i < collapsedHeaders.length; i += 1) {
        const dbText = row?.[i * 2];
        const origText = row?.[i * 2 + 1];
        const cell = expandCell(dbText, origText);
        cells.push(cell);
        if (cell.isDifferent) hasAnyDifference = true;
      }
      return { key: `${page}-${idx}`, cells, hasAnyDifference };
    });
  }, [rows, collapsedHeaders, page]);

  if (error) {
    return (
      <div className="card h-100">
        <div className="card-header">
          <strong>Row-by-row comparison</strong>
        </div>
        <div className="card-body">
          <Alert color="danger" fade={false}>{error}</Alert>
        </div>
      </div>
    );
  }

  if (!collapsedHeaders || collapsedHeaders.length === 0) {
    return (
      <div className="card h-100">
        <div className="card-header">
          <strong>Row-by-row comparison</strong>
        </div>
        <div className="card-body text-center py-5 text-muted">
          No columns available for comparison.
        </div>
      </div>
    );
  }

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
        <strong>Row-by-row comparison (DB | Original CTRAC)</strong>
        <small className="text-muted">
          {differencesOnly ? 'Showing only rows with at least one difference' : 'Showing all rows'}
          {' '}· {totalRowCount || 0} total
          {builtAtUtc && (
            <> · cache built {formatDateTime(builtAtUtc)}</>
          )}
        </small>
      </div>
      <div className="card-body p-0">
        <div className="model-spreadsheet-scroll">
          <table className="table table-sm table-bordered mb-0 model-spreadsheet-table consolidated-compare-table">
            <thead>
              <tr>
                <th className="row-index-col">#</th>
                {collapsedHeaders.map((col) => {
                  const meta = col.meta;
                  const label = meta?.Label || stripHeaderPrefix(col.dbHeader, 'DB-') || stripHeaderPrefix(col.origHeader, 'Original-') || '';
                  const title = meta?.Title || '';
                  const formula = meta?.Formula || '';
                  const tag = meta?.Tag || '';
                  return (
                    <th
                      key={col.key}
                      title={`${label}${title ? ' · ' + title : ''}${formula ? '\n' + formula : ''}${tag ? '\nTag: ' + tag : ''}`}
                    >
                      <div className="model-spreadsheet-col-header">
                        <span className="model-spreadsheet-col-label">{label}</span>
                        {title && (
                          <span className="model-spreadsheet-col-title">{title}</span>
                        )}
                        {tag && (
                          <span className="badge badge-info model-spreadsheet-col-tag">
                            {tag}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rowsWithFlags.map((row, rowIdx) => (
                <tr key={row.key} className={row.hasAnyDifference ? 'consolidated-diff-row' : ''}>
                  <td className="row-index-col">{page * pageSize + rowIdx + 1}</td>
                  {row.cells.map((cell, ci) => {
                    return (
                      <td
                        key={ci}
                        className={
                          'model-spreadsheet-cell consolidated-compare-cell' +
                          (cell.isDifferent ? ' consolidated-diff-cell' : '')
                        }
                      >
                        {cell.display}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {rowsWithFlags.length === 0 && (
                <tr>
                  <td colSpan={collapsedHeaders.length + 1} className="text-center text-muted py-4">
                    {differencesOnly
                      ? 'No rows with differences — every row matched on every column.'
                      : 'No rows to display.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-footer d-flex justify-content-between align-items-center flex-wrap">
        <div className="text-muted small">
          Rows are aligned by RowIndex from the source spreadsheets.
          {' '}· Cells highlighted{' '}
          <span className="consolidated-legend-swatch">light red</span>{' '}
          when values differ between DB and Original CTRAC.
        </div>
        <div className="text-muted small d-flex align-items-center flex-wrap">
          <span className="d-inline-flex align-items-center">
            DB #{dbCalc?.CalculationDbId || '—'}
            {dbCalc?.WarningsCount > 0 && (
              <RemovedRowsWarningIcon
                count={dbCalc.WarningsCount}
                onClick={() =>
                  onShowRemovedRows &&
                  onShowRemovedRows({
                    calculationDbId: dbCalc.CalculationDbId,
                    calculationTitle: 'DB',
                    dataSourceLabel: DB,
                  })
                }
                titlePrefix="DB"
              />
            )}
          </span>
          <span className="mx-3">·</span>
          <span className="d-inline-flex align-items-center">
            Original #{origCalc?.CalculationDbId || '—'}
            {origCalc?.WarningsCount > 0 && (
              <RemovedRowsWarningIcon
                count={origCalc.WarningsCount}
                onClick={() =>
                  onShowRemovedRows &&
                  onShowRemovedRows({
                    calculationDbId: origCalc.CalculationDbId,
                    calculationTitle: 'Original CTRAC',
                    dataSourceLabel: ORIGINAL,
                  })
                }
                titlePrefix="Original CTRAC"
              />
            )}
          </span>
          <span className="mx-3">·</span>
          Page {page + 1} of {totalPages} ({totalRowCount || 0} rows)
          {loading && <span className="ms-2 text-muted"><Spinner size="sm" /> Loading…</span>}
        </div>
        <div className="btn-group">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 0 || loading}
            onClick={() => onPageChange(0)}
          >
            «
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 0 || loading}
            onClick={() => onPageChange(Math.max(0, page - 1))}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page >= totalPages - 1 || loading}
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          >
            Next ›
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page >= totalPages - 1 || loading}
            onClick={() => onPageChange(totalPages - 1)}
          >
            »
          </button>
        </div>
        <div className="d-flex align-items-center mt-2 mt-md-0 ms-md-2 w-100 w-md-auto">
          <label
            htmlFor="consolidated-page-jump"
            className="text-muted small me-2 mb-0"
          >
            Go to page
          </label>
          <input
            id="consolidated-page-jump"
            type="number"
            min={1}
            max={totalPages}
            step={1}
            className="form-control form-control-sm"
            style={{ width: '5rem' }}
            value={pageInputValue}
            disabled={loading || totalPages <= 1}
            onChange={(e) => setPageInputValue(e.target.value)}
            onBlur={commitPageInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitPageInput();
              }
            }}
            aria-label={`Jump to page (1 to ${totalPages})`}
            title={`Enter a page number between 1 and ${totalPages}`}
          />
          <span className="text-muted small ms-2">/ {totalPages}</span>
        </div>
      </div>
    </div>
  );
}

function SpreadsheetSidePanel({
  title,
  dataSource,
  calculation,
  pageSize,
  page,
  setPage,
  loading,
  error,
  onShowRemovedRows,
}) {
  if (!calculation) {
    return (
      <div className="card h-100">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{title}</strong>
        </div>
        <div className="card-body text-muted text-center py-5">
          {loading ? <Spinner size="sm" /> : 'Pick or compute a calculation to display.'}
        </div>
      </div>
    );
  }

  if (calculation.Error && !calculation.CalculationDbId) {
    return (
      <div className="card h-100">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{title}</strong>
        </div>
        <div className="card-body">
          <Alert color="warning" fade={false}>
            <strong>Calculation failed:</strong> {calculation.Error}
          </Alert>
        </div>
      </div>
    );
  }

  const columns = calculation.Columns || [];
  const rows = calculation.Rows || [];
  const totalRows = calculation.RowCount || rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
        <strong>
          {title}
          {calculation.WarningsCount > 0 && (
            <RemovedRowsWarningIcon
              count={calculation.WarningsCount}
              onClick={() =>
                onShowRemovedRows &&
                onShowRemovedRows({
                  calculationDbId: calculation.CalculationDbId,
                  calculationTitle: title,
                  dataSourceLabel: dataSource,
                })
              }
              titlePrefix={`${title}`}
            />
          )}
        </strong>
        <small className="text-muted">
          #{calculation.CalculationDbId} · {columns.length} cols × {totalRows} rows
        </small>
      </div>
      <div className="card-body p-0">
        {error && (
          <Alert color="danger" className="m-2" fade={false}>
            {error}
          </Alert>
        )}
        {columns.length === 0 ? (
          <div className="p-3 text-muted text-center">
            {loading ? <Spinner size="sm" /> : 'No columns in this calculation.'}
          </div>
        ) : (
          <div className="model-spreadsheet-scroll">
            <table className="table table-sm table-bordered mb-0 model-spreadsheet-table">
              <thead>
                <tr>
                  <th className="row-index-col">#</th>
                  {columns.map((col) => (
                    <th
                      key={col.Index}
                      title={`${col.Label}${col.Title ? ' · ' + col.Title : ''}${
                        col.Formula ? '\n' + col.Formula : ''
                      }${col.Tag ? '\nTag: ' + col.Tag : ''}`}
                    >
                      <div className="model-spreadsheet-col-header">
                        <span className="model-spreadsheet-col-label">{col.Label}</span>
                        {col.Title && (
                          <span className="model-spreadsheet-col-title">{col.Title}</span>
                        )}
                        {col.Tag && (
                          <span className="badge badge-info model-spreadsheet-col-tag">
                            {col.Tag}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.Index}>
                    <td className="row-index-col">{row.Index + 1}</td>
                    {(row.Cells || []).map((cell, ci) => {
                      const bg = colorToCss(cell.Color);
                      return (
                        <td
                          key={ci}
                          className="model-spreadsheet-cell"
                          style={bg ? { backgroundColor: bg } : undefined}
                        >
                          {cell.Text}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length + 1} className="text-center text-muted">
                      {loading ? 'Loading…' : 'No rows on this page.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card-footer d-flex justify-content-between align-items-center flex-wrap">
        <div>
          Page {page + 1} of {totalPages} ({totalRows} rows)
          {loading && <span className="ms-2 text-muted"><Spinner size="sm" /> Loading…</span>}
        </div>
        <div className="btn-group">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 0 || loading}
            onClick={() => setPage(0)}
          >
            «
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page === 0 || loading}
            onClick={() => setPage(Math.max(0, page - 1))}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page >= totalPages - 1 || loading}
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          >
            Next ›
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={page >= totalPages - 1 || loading}
            onClick={() => setPage(totalPages - 1)}
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}

export const AdminCompareSpreadsheets = () => {
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);

  // Recent DB / OriginalCTRAC calculations for the selected model.
  const [recentDb, setRecentDb] = useState([]);
  const [recentOriginal, setRecentOriginal] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(false);
  const [hasExistingPair, setHasExistingPair] = useState(false);

  // Comparison state.
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);

  // Left/right side state.
  const [dbCalc, setDbCalc] = useState(null); // slim (header) object: { CalculationDbId, DataSource, ... }
  const [origCalc, setOrigCalc] = useState(null);

  // Rows + pagination per side.
  const [dbRows, setDbRows] = useState([]);
  const [origRows, setOrigRows] = useState([]);
  const [dbLoading, setDbLoading] = useState(false);
  const [origLoading, setOrigLoading] = useState(false);
  const [dbRowsError, setDbRowsError] = useState(null);
  const [origRowsError, setOrigRowsError] = useState(null);

  const [pageSize, setPageSize] = useState(50);
  const [dbPage, setDbPage] = useState(0);
  const [origPage, setOrigPage] = useState(0);

  // Cell-content flavor for the row-by-row comparison view:
  //   'display' → formatted text + text-equality diff rule (default)
  //   'raw'     → underlying float + bit-exact diff rule
  // Switches which cached CSV the comparison-rows endpoint reads.
  const [comparisonMode, setComparisonMode] = useState('display');

  // Row-by-row comparison view state.
  //   'all'         → original side-by-side paginated grids (default)
  //   'consolidated' → single merged table keyed on the Date column
  //   'differences' → same as consolidated but only rows with at least one diff
  const [viewMode, setViewMode] = useState(VIEW_MODE_ALL);

  // Backend-built CSV cache metadata. Set when the "DB vs Original CTRAC"
  // button completes successfully (and the comparison rows were built).
  const [comparisonBuiltAtUtc, setComparisonBuiltAtUtc] = useState(null);
  const [comparisonConsolidatedRowCount, setComparisonConsolidatedRowCount] = useState(null);
  const [comparisonDifferencesRowCount, setComparisonDifferencesRowCount] = useState(null);
  const [comparisonRawConsolidatedRowCount, setComparisonRawConsolidatedRowCount] = useState(null);
  const [comparisonRawDifferencesRowCount, setComparisonRawDifferencesRowCount] = useState(null);
  const [comparisonError, setComparisonError] = useState(null);

  // One page from the cached CSV. Headers + Columns are constant across
  // pages of the same (model, mode) pair; only Rows change.
  const [comparisonHeaders, setComparisonHeaders] = useState([]);
  const [comparisonColumns, setComparisonColumns] = useState([]);
  const [comparisonRows, setComparisonRows] = useState([]);
  const [comparisonTotalRowCount, setComparisonTotalRowCount] = useState(0);
  const [comparisonDateColumnLabel, setComparisonDateColumnLabel] = useState('');
  const [comparisonPage, setComparisonPage] = useState(0);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonRowsError, setComparisonRowsError] = useState(null);

  // Modal state for the lazy-fetched warnings dialog. Tracks the
  // calculation id the user clicked on along with display labels so the
  // modal can show context (DB vs Original CTRAC, etc.) in its header.
  const [warningsModal, setWarningsModal] = useState(null);

  const openWarningsModal = (info) => {
    if (!info || !info.calculationDbId) return;
    setWarningsModal({
      calculationDbId: info.calculationDbId,
      calculationTitle: info.calculationTitle || null,
      dataSourceLabel: info.dataSourceLabel || null,
    });
  };

  const closeWarningsModal = () => {
    setWarningsModal(null);
  };

  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedModelId) {
      loadRecentForModel(parseInt(selectedModelId, 10));
    } else {
      setRecentDb([]);
      setRecentOriginal([]);
      setHasExistingPair(false);
    }
    setDbCalc(null);
    setOrigCalc(null);
    setDbRows([]);
    setOrigRows([]);
    setError(null);
    setDbPage(0);
    setOrigPage(0);
    setComparisonBuiltAtUtc(null);
    setComparisonConsolidatedRowCount(null);
    setComparisonDifferencesRowCount(null);
    setComparisonRawConsolidatedRowCount(null);
    setComparisonRawDifferencesRowCount(null);
    setComparisonError(null);
    setComparisonHeaders([]);
    setComparisonColumns([]);
    setComparisonRows([]);
    setComparisonTotalRowCount(0);
    setComparisonDateColumnLabel('');
    setComparisonPage(0);
    setComparisonMode('display');
    setComparisonRowsError(null);
    setWarningsModal(null);
  }, [selectedModelId]);

  async function loadModels() {
    try {
      setLoadingModels(true);
      const list = await apiClient.getModelsList();
      setModels(list || []);
      if (list && list.length > 0 && !selectedModelId) {
        setSelectedModelId(list[0].Id?.toString() || '');
      }
    } catch (exc) {
      console.error('Failed to load models', exc);
      setError(exc.message || 'Failed to load models');
    } finally {
      setLoadingModels(false);
    }
  }

  async function loadRecentForModel(modelId) {
    if (!modelId) return;
    setLoadingRecent(true);
    try {
      const [dbResp, origResp] = await Promise.all([
        apiClient.getRecentModelSpreadsheetsBySource(modelId, DB, 5),
        apiClient.getRecentModelSpreadsheetsBySource(modelId, ORIGINAL, 5),
      ]);
      const dbItems = dbResp?.Items || [];
      const origItems = origResp?.Items || [];
      setRecentDb(dbItems);
      setRecentOriginal(origItems);
      setHasExistingPair(dbItems.length > 0 && origItems.length > 0);
    } catch (exc) {
      console.error('Failed to load recent spreadsheets', exc);
      setRecentDb([]);
      setRecentOriginal([]);
      setHasExistingPair(false);
    } finally {
      setLoadingRecent(false);
    }
  }

  async function fetchSideRows(calcId, setRows, setLoadingFn, setErrFn, pageIdx) {
    if (!calcId) return;
    setLoadingFn(true);
    setErrFn(null);
    try {
      const data = await apiClient.getModelSpreadsheetRows(calcId, {
        startRow: pageIdx * pageSize,
        limit: pageSize,
      });
      // Sort by Index ascending for a stable display order.
      const rows = (data.Rows || []).slice().sort(
        (a, b) => (a.Index ?? 0) - (b.Index ?? 0)
      );
      setRows(rows);
    } catch (exc) {
      console.error('Failed to load spreadsheet rows', exc);
      setErrFn(exc.message || 'Failed to load rows');
      setRows([]);
    } finally {
      setLoadingFn(false);
    }
  }

  // Re-fetch the current page when page or pageSize changes.
  useEffect(() => {
    if (dbCalc?.CalculationDbId) {
      fetchSideRows(dbCalc.CalculationDbId, setDbRows, setDbLoading, setDbRowsError, dbPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbPage, pageSize, dbCalc?.CalculationDbId]);

  useEffect(() => {
    if (origCalc?.CalculationDbId) {
      fetchSideRows(origCalc.CalculationDbId, setOrigRows, setOrigLoading, setOrigRowsError, origPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origPage, pageSize, origCalc?.CalculationDbId]);

  async function handleCompareFresh() {
    if (!selectedModelId) {
      setError('Please select a model first');
      return;
    }
    setComputing(true);
    setError(null);
    setDbCalc(null);
    setOrigCalc(null);
    setDbRows([]);
    setOrigRows([]);
    setDbPage(0);
    setOrigPage(0);
    setComparisonBuiltAtUtc(null);
    setComparisonConsolidatedRowCount(null);
    setComparisonDifferencesRowCount(null);
    setComparisonRawConsolidatedRowCount(null);
    setComparisonRawDifferencesRowCount(null);
    setComparisonError(null);
    setComparisonHeaders([]);
    setComparisonColumns([]);
    setComparisonRows([]);
    setComparisonTotalRowCount(0);
    setComparisonDateColumnLabel('');
    setComparisonPage(0);
    setComparisonMode('display');
    setComparisonRowsError(null);
    setWarningsModal(null);
    try {
      const resp = await apiClient.compareModelSpreadsheets(parseInt(selectedModelId, 10), { invalidateCache: true });
      const db = resp?.Db || {};
      const orig = resp?.OriginalCtrac || {};

      // Build slim calc headers (the compare endpoint returns the slim shape).
      setDbCalc({
        CalculationDbId: db.CalculationDbId,
        CalculationId: db.CalculationId,
        DataSource: DB,
        ColumnCount: db.ColumnCount,
        RowCount: db.RowCount,
        ExecutionDuration: db.ExecutionDuration,
        Columns: [], // populated by the rows fetch
        Error: db.Error,
        WarningsCount: db.WarningsCount || 0,
        _loadedColumns: [],
      });
      setOrigCalc({
        CalculationDbId: orig.CalculationDbId,
        CalculationId: orig.CalculationId,
        DataSource: ORIGINAL,
        ColumnCount: orig.ColumnCount,
        RowCount: orig.RowCount,
        ExecutionDuration: orig.ExecutionDuration,
        Columns: [],
        Error: orig.Error,
        WarningsCount: orig.WarningsCount || 0,
        _loadedColumns: [],
      });

      // Record the comparison CSV cache metadata the server returned.
      // Switching modes after this is just a file read.
      if (resp?.ComparisonConsolidatedRowCount != null) {
        setComparisonBuiltAtUtc(resp.ComparisonBuiltAtUtc || new Date().toISOString());
        setComparisonConsolidatedRowCount(resp.ComparisonConsolidatedRowCount);
        setComparisonDifferencesRowCount(resp.ComparisonDifferencesRowCount);
        setComparisonRawConsolidatedRowCount(resp.ComparisonRawConsolidatedRowCount ?? null);
        setComparisonRawDifferencesRowCount(resp.ComparisonRawDifferencesRowCount ?? null);
      }
      setComparisonError(resp?.ComparisonError || null);
    } catch (exc) {
      console.error('Compare failed', exc);
      setError(exc.message || 'Failed to compare spreadsheets');
    } finally {
      setComputing(false);
    }
  }

  async function handleCompareExisting() {
    if (!selectedModelId) {
      setError('Please select a model first');
      return;
    }
    if (recentDb.length === 0 || recentOriginal.length === 0) {
      setError('Need at least one DB and one OriginalCTRAC calculation to compare existing.');
      return;
    }
    setError(null);
    // Use the most-recent of each side.
    const db = recentDb[0];
    const orig = recentOriginal[0];
    setComputing(true);
    try {
      setDbCalc({
        CalculationDbId: db.Id,
        CalculationId: db.CalculationId,
        DataSource: DB,
        ColumnCount: db.ColumnCount,
        RowCount: db.RowCount,
        ExecutionDuration: db.ExecutionDuration,
        Columns: db.Columns || [],
        Error: null,
        WarningsCount: db.WarningsCount || 0,
      });
      setOrigCalc({
        CalculationDbId: orig.Id,
        CalculationId: orig.CalculationId,
        DataSource: ORIGINAL,
        ColumnCount: orig.ColumnCount,
        RowCount: orig.RowCount,
        ExecutionDuration: orig.ExecutionDuration,
        Columns: orig.Columns || [],
        Error: null,
        WarningsCount: orig.WarningsCount || 0,
      });
      setDbPage(0);
      setOrigPage(0);
      setComparisonBuiltAtUtc(null);
      setComparisonConsolidatedRowCount(null);
      setComparisonDifferencesRowCount(null);
      setComparisonError(null);
      setComparisonHeaders([]);
      setComparisonColumns([]);
      setComparisonRows([]);
      setComparisonTotalRowCount(0);
      setComparisonDateColumnLabel('');
      setComparisonPage(0);
      setComparisonMode('display');
      setComparisonRowsError(null);
      setWarningsModal(null);

      // Route the existing-pairs workflow through the same backend cache
      // as the fresh-compute workflow. If the CSV files already exist on
      // disk the server reuses them (no DB reload); otherwise it builds
      // them from the supplied calculation ids in one round-trip.
      const resp = await apiClient.compareModelSpreadsheets(
        parseInt(selectedModelId, 10),
        {
          existingDbCalculationDbId: db.Id,
          existingOriginalCalculationDbId: orig.Id,
        }
      );
      setComparisonConsolidatedRowCount(
        resp?.ComparisonConsolidatedRowCount ?? null
      );
      setComparisonDifferencesRowCount(
        resp?.ComparisonDifferencesRowCount ?? null
      );
      setComparisonRawConsolidatedRowCount(
        resp?.ComparisonRawConsolidatedRowCount ?? null
      );
      setComparisonRawDifferencesRowCount(
        resp?.ComparisonRawDifferencesRowCount ?? null
      );
      setComparisonBuiltAtUtc(resp?.ComparisonBuiltAtUtc || new Date().toISOString());
      setComparisonError(resp?.ComparisonError || null);
    } catch (exc) {
      console.error('Compare existing failed', exc);
      setError(exc.message || 'Failed to compare existing spreadsheets');
    } finally {
      setComputing(false);
    }
  }

  // Build a "current state" object that includes columns + rows for each side
  // so the SpreadsheetSidePanel can render them with one set of props.
  const dbView = useMemo(() => {
    if (!dbCalc) return null;
    return {
      ...dbCalc,
      Columns: dbCalc.Columns?.length ? dbCalc.Columns : (dbRows[0] ? [] : []),
      Rows: dbRows,
    };
  }, [dbCalc, dbRows]);

  const origView = useMemo(() => {
    if (!origCalc) return null;
    return {
      ...origCalc,
      Columns: origCalc.Columns?.length ? origCalc.Columns : [],
      Rows: origRows,
    };
  }, [origCalc, origRows]);

  // Whenever the rows payload arrives it carries column metadata. Propagate
  // it back into the calc object so the header is rendered correctly.
  useEffect(() => {
    if (!dbCalc?.CalculationDbId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.getModelSpreadsheetRows(dbCalc.CalculationDbId, {
          startRow: 0,
          limit: 1,
        });
        if (cancelled) return;
        setDbCalc((prev) =>
          prev && prev.CalculationDbId === dbCalc.CalculationDbId
            ? {
                ...prev,
                ColumnCount: data.ColumnCount ?? prev.ColumnCount,
                RowCount: data.RowCount ?? prev.RowCount,
                DataSource: data.DataSource ?? prev.DataSource,
                // WarningsCount is supplied by /api/spreadsheets/compare
                // (or /api/spreadsheets/{id}/recent-by-source); the
                // rows endpoint intentionally does not carry it.
                Columns: data.Columns || prev.Columns || [],
              }
            : prev
        );
      } catch (exc) {
        // ignore; page-fetch will surface its own error
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbCalc?.CalculationDbId]);

  useEffect(() => {
    if (!origCalc?.CalculationDbId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.getModelSpreadsheetRows(origCalc.CalculationDbId, {
          startRow: 0,
          limit: 1,
        });
        if (cancelled) return;
        setOrigCalc((prev) =>
          prev && prev.CalculationDbId === origCalc.CalculationDbId
            ? {
                ...prev,
                ColumnCount: data.ColumnCount ?? prev.ColumnCount,
                RowCount: data.RowCount ?? prev.RowCount,
                DataSource: data.DataSource ?? prev.DataSource,
                // WarningsCount is supplied by /api/spreadsheets/compare
                // (or /api/spreadsheets/{id}/recent-by-source); the
                // rows endpoint intentionally does not carry it.
                Columns: data.Columns || prev.Columns || [],
              }
            : prev
        );
      } catch (exc) {
        // ignore; page-fetch will surface its own error
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origCalc?.CalculationDbId]);

  // Fetch one page from the backend-built comparison CSV. The CSV is the
  // single source of truth for both row-by-row modes — switching modes
  // just re-issues a request with a different `Mode` value, never re-runs
  // the spreadsheet compute.
  async function fetchComparisonPage(modelId, mode, pageIdx, comparisonModeArg) {
    if (!modelId || !mode) return;
    setComparisonLoading(true);
    setComparisonRowsError(null);
    try {
      const data = await apiClient.getModelComparisonRows(modelId, mode, {
        comparisonMode: comparisonModeArg,
        startRow: pageIdx * pageSize,
        limit: pageSize,
      });
      setComparisonHeaders(data.Headers || []);
      setComparisonColumns(data.Columns || []);
      setComparisonRows(data.Rows || []);
      setComparisonTotalRowCount(data.TotalRowCount || 0);
      setComparisonDateColumnLabel(data.DateColumnLabel || '');
      setComparisonBuiltAtUtc(data.BuiltAtUtc || null);
    } catch (exc) {
      console.error('Failed to load comparison CSV page', exc);
      setComparisonRowsError(exc.message || 'Failed to load comparison CSV');
      setComparisonRows([]);
    } finally {
      setComparisonLoading(false);
    }
  }

  // Whenever the user is in a row-by-row mode and the cache is present
  // (i.e. they clicked "DB vs Original CTRAC" successfully) re-fetch the
  // current page. Switching modes (Consolidated/Differences) or comparison
  // mode (Display/Raw) flips this effect and re-reads the matching CSV on
  // the backend.
  const consolidatedActive = viewMode === VIEW_MODE_CONSOLIDATED || viewMode === VIEW_MODE_DIFFERENCES;
  useEffect(() => {
    if (!consolidatedActive) return;
    if (!dbCalc?.CalculationDbId || !origCalc?.CalculationDbId) return;
    if (!comparisonBuiltAtUtc) return;
    const mode =
      viewMode === VIEW_MODE_DIFFERENCES ? 'differences' : 'consolidated';
    fetchComparisonPage(parseInt(selectedModelId, 10), mode, comparisonPage, comparisonMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    consolidatedActive,
    viewMode,
    comparisonPage,
    pageSize,
    comparisonMode,
    comparisonBuiltAtUtc,
    dbCalc?.CalculationDbId,
    origCalc?.CalculationDbId,
    selectedModelId,
  ]);

  const selectedModel = useMemo(
    () => models.find((m) => m.Id?.toString() === selectedModelId) || null,
    [models, selectedModelId]
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Compare Spreadsheets</h2>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Compare DB vs Original CTRAC</h5>
        </div>
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-6">
              <label className="form-label">Model</label>
              <select
                className="form-control"
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                disabled={loadingModels || computing}
              >
                <option value="">-- Select a model --</option>
                {models.map((model) => (
                  <option key={model.Id} value={model.Id}>
                    {model.Name || model.FileName || `Model #${model.Id}`} (ID: {model.Id})
                  </option>
                ))}
              </select>
              {selectedModel && (
                <small className="text-muted">
                  File: {selectedModel.FileName || '—'}
                </small>
              )}
              <div className="mt-2">
                <label className="form-label">Rows per page</label>
                <select
                  className="form-control"
                  style={{ maxWidth: 200 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setDbPage(0);
                    setOrigPage(0);
                  }}
                  disabled={computing}
                >
                  {[25, 50, 100, 500].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="mt-2">
                <label className="form-label">Comparison cell content</label>
                <select
                  className="form-control"
                  style={{ maxWidth: 280 }}
                  value={comparisonMode}
                  onChange={(e) => {
                    setComparisonMode(e.target.value);
                    setComparisonPage(0);
                  }}
                  disabled={computing}
                  title="Choose what the comparison caches: formatted column text (Display) or the underlying float (Raw)."
                >
                  <option value="display">Use Display Text comparison</option>
                  <option value="raw">Use raw comparison</option>
                </select>
                {comparisonMode === 'raw' && (
                  <small className="text-muted d-block mt-1">
                    Bit-exact float comparison — single-ULP drift between DB and Original CTRAC will be marked as a diff.
                  </small>
                )}
                {comparisonMode === 'display' && (
                  <small className="text-muted d-block mt-1">
                    Formatted text comparison — ignores 1-ULP float drift that would otherwise flip a single rounding digit.
                  </small>
                )}
              </div>
              <div className="mt-2">
                <label className="form-label d-block">Comparison view</label>
                <div className="btn-group btn-group-sm" role="group" aria-label="Comparison view">
                  <button
                    type="button"
                    className={
                      'btn btn-outline-secondary' +
                      (viewMode === VIEW_MODE_ALL ? ' active' : '')
                    }
                    onClick={() => setViewMode(VIEW_MODE_ALL)}
                    disabled={computing}
                    title="Show DB and Original CTRAC side-by-side as paginated grids"
                  >
                    Show all
                  </button>
                  <button
                    type="button"
                    className={
                      'btn btn-outline-secondary' +
                      (viewMode === VIEW_MODE_CONSOLIDATED ? ' active' : '')
                    }
                    onClick={() => {
                      setComparisonPage(0);
                      setViewMode(VIEW_MODE_CONSOLIDATED);
                    }}
                    disabled={computing || !comparisonBuiltAtUtc || !!comparisonError}
                    title={
                      comparisonBuiltAtUtc
                        ? (comparisonMode === 'raw'
                            ? comparisonRawConsolidatedRowCount != null
                              ? `Read from cached Raw CSV (${comparisonRawConsolidatedRowCount} rows)`
                              : 'Read from cached Raw CSV'
                            : comparisonConsolidatedRowCount != null
                              ? `Read from cached Display CSV (${comparisonConsolidatedRowCount} rows)`
                              : 'Read from cached Display CSV')
                        : 'Click "DB vs Original CTRAC" first to build the comparison CSV cache'
                    }
                  >
                    Consolidated table
                    {((comparisonMode === 'raw' && comparisonRawConsolidatedRowCount != null) ||
                      (comparisonMode !== 'raw' && comparisonConsolidatedRowCount != null)) && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                        {comparisonMode === 'raw'
                          ? comparisonRawConsolidatedRowCount
                          : comparisonConsolidatedRowCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={
                      'btn btn-outline-secondary' +
                      (viewMode === VIEW_MODE_DIFFERENCES ? ' active' : '')
                    }
                    onClick={() => {
                      setComparisonPage(0);
                      setViewMode(VIEW_MODE_DIFFERENCES);
                    }}
                    disabled={computing || !comparisonBuiltAtUtc || !!comparisonError}
                    title={
                      comparisonBuiltAtUtc
                        ? (comparisonMode === 'raw'
                            ? comparisonRawDifferencesRowCount != null
                              ? `Read from cached Raw CSV (${comparisonRawDifferencesRowCount} rows)`
                              : 'Read from cached Raw CSV'
                            : comparisonDifferencesRowCount != null
                              ? `Read from cached Display CSV (${comparisonDifferencesRowCount} rows)`
                              : 'Read from cached Display CSV')
                        : 'Click "DB vs Original CTRAC" first to build the comparison CSV cache'
                    }
                  >
                    Only differences
                    {((comparisonMode === 'raw' && comparisonRawDifferencesRowCount != null) ||
                      (comparisonMode !== 'raw' && comparisonDifferencesRowCount != null)) && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.875rem' }}>
                        {comparisonMode === 'raw'
                          ? comparisonRawDifferencesRowCount
                          : comparisonDifferencesRowCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <Button
                color="primary"
                onClick={handleCompareFresh}
                disabled={computing || loadingModels || !selectedModelId}
                block
              >
                {computing ? (
                  <><Spinner size="sm" className="me-2" />Comparing…</>
                ) : (
                  'DB vs Original CTRAC'
                )}
              </Button>
              <Button
                color="secondary"
                onClick={handleCompareExisting}
                disabled={
                  computing || loadingModels || !selectedModelId || !hasExistingPair
                }
                block
                className="mt-2"
                title={
                  hasExistingPair
                    ? 'Open the most recent DB and OriginalCTRAC calculations side-by-side'
                    : 'Need at least one DB and one OriginalCTRAC calculation for the selected model'
                }
              >
                Compare using existing calculations
              </Button>
            </div>
          </div>

          {error && (
            <Alert color="danger" className="mt-3" fade={false}>
              {error}
            </Alert>
          )}
        </div>
      </div>

      {selectedModelId && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Recent calculations for selected model</h5>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => loadRecentForModel(parseInt(selectedModelId, 10))}
              disabled={loadingRecent}
            >
              {loadingRecent ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-6 border-right">
                <div className="p-2">
                  <strong>DB (live candle-derived CTRAC)</strong>
                </div>
                {loadingRecent ? (
                  <div className="p-3 text-muted">Loading…</div>
                ) : recentDb.length === 0 ? (
                  <div className="p-3 text-muted">No DB calculations for this model.</div>
                ) : (
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Calc #</th>
                        <th>Created</th>
                        <th>Duration</th>
                        <th>Cols × Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentDb.map((calc) => (
                        <tr key={calc.Id}>
                          <td>#{calc.Id}</td>
                          <td>{calc.getFormattedCreateTime()}</td>
                          <td>{formatDuration(calc.ExecutionDuration)}</td>
                          <td>{calc.ColumnCount} × {calc.RowCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="col-md-6">
                <div className="p-2">
                  <strong>Original CTRAC (user-uploaded package)</strong>
                </div>
                {loadingRecent ? (
                  <div className="p-3 text-muted">Loading…</div>
                ) : recentOriginal.length === 0 ? (
                  <div className="p-3 text-muted">No OriginalCTRAC calculations for this model.</div>
                ) : (
                  <table className="table table-sm table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Calc #</th>
                        <th>Created</th>
                        <th>Duration</th>
                        <th>Cols × Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOriginal.map((calc) => (
                        <tr key={calc.Id}>
                          <td>#{calc.Id}</td>
                          <td>{calc.getFormattedCreateTime()}</td>
                          <td>{formatDuration(calc.ExecutionDuration)}</td>
                          <td>{calc.ColumnCount} × {calc.RowCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {consolidatedActive && (dbCalc?.CalculationDbId || origCalc?.CalculationDbId) && (
        <div className="row">
          <div className="col-12 mb-3">
            {comparisonError && (
              <Alert color="danger" fade={false} className="mb-2">
                <strong>Comparison cache could not be built:</strong> {comparisonError}
              </Alert>
            )}
            <ConsolidatedComparisonPanel
              dbCalc={dbCalc}
              origCalc={origCalc}
              dateColumnLabel={comparisonDateColumnLabel}
              columns={comparisonColumns}
              headers={comparisonHeaders}
              rows={comparisonRows}
              totalRowCount={comparisonTotalRowCount}
              page={comparisonPage}
              pageSize={pageSize}
              loading={comparisonLoading}
              error={comparisonRowsError}
              differencesOnly={viewMode === VIEW_MODE_DIFFERENCES}
              builtAtUtc={comparisonBuiltAtUtc}
              onPageChange={setComparisonPage}
              onShowRemovedRows={openWarningsModal}
            />
          </div>
        </div>
      )}

      {!consolidatedActive && (dbView || origView) && (
        <div className="row">
          <div className="col-md-6 mb-3">
            <SpreadsheetSidePanel
              title="DB"
              dataSource={DB}
              calculation={dbView}
              pageSize={pageSize}
              page={dbPage}
              setPage={setDbPage}
              loading={dbLoading}
              error={dbRowsError}
              onShowRemovedRows={openWarningsModal}
            />
          </div>
          <div className="col-md-6 mb-3">
            <SpreadsheetSidePanel
              title="Original CTRAC"
              dataSource={ORIGINAL}
              calculation={origView}
              pageSize={pageSize}
              page={origPage}
              setPage={setOrigPage}
              loading={origLoading}
              error={origRowsError}
              onShowRemovedRows={openWarningsModal}
            />
          </div>
        </div>
      )}

      {warningsModal && (
        <WarningsModal
          isOpen={!!warningsModal}
          toggle={closeWarningsModal}
          calculationDbId={warningsModal.calculationDbId}
          calculationTitle={warningsModal.calculationTitle}
          dataSourceLabel={warningsModal.dataSourceLabel}
        />
      )}
    </div>
  );
};

export default AdminCompareSpreadsheets;